import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, viewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStep, WizardStepComponent } from 'mbs-ui-kit';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import { catchError, EMPTY, from, Observable, of, switchMap } from 'rxjs';
import { CompanyWizardFacade } from 'wizards/services/entity-wizards/company/company-wizard.facade';
import {
  CompanyAdministratorsStepDefaultValue,
  CompanyAdministratorsStepValue,
  CompanyBackupTemplatesStepDefaultValue,
  CompanyBackupTemplatesStepValue,
  CompanyDownloadStepDefaultValue,
  CompanyDownloadStepValue,
  CompanyNameStepDefaultValue,
  CompanyNameStepValue
} from './models/company-wizard-models';
import { CompanyAdministratorsStepComponent } from './steps/company-administrators-step/company-administrators-step.component';
import { CompanyBackupTemplatesStepComponent } from './steps/company-backup-templates-step/company-backup-templates-step.component';
import { CompanyDestinationsStepComponent } from './steps/company-destinations-step/company-destinations-step.component';
import { CompanyDownloadStepComponent } from './steps/company-download-step/company-download-step.component';
import { CompanyNameStepComponent } from './steps/company-name-step/company-name-step.component';
import { CompanySummaryStepComponent } from './steps/company-summary-step/company-summary-step.component';

@Component({
  selector: 'mbs-company-wizard',
  templateUrl: './company-wizard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CompanyAdministratorsStepComponent,
    CompanyBackupTemplatesStepComponent,
    CompanyDestinationsStepComponent,
    CompanyDownloadStepComponent,
    CompanyNameStepComponent,
    CompanySummaryStepComponent,
    FormsModule,
    I18NextPipe,
    WizardComponent,
    WizardStepComponent
  ]
})
export class CompanyWizardComponent {
  protected readonly ability = inject(AbilityService);
  private i18nPipe = inject(I18NextPipe);
  private readonly companyWizardFacade = inject(CompanyWizardFacade);

  private readonly wizard = viewChild<WizardComponent>(WizardComponent);
  private readonly backupTemplatesStepComponent = viewChild(CompanyBackupTemplatesStepComponent);

  public readonly currentStep: WritableSignal<WizardStep> = signal(null);
  public readonly innerLoading: WritableSignal<boolean> = signal(false);
  public readonly wizardFinish: WritableSignal<boolean> = signal(false);
  public readonly PermissionsEnum = PermissionsEnum;
  public readonly elementSelectors = {
    name: {
      administratorsStep: 'company-wizard-administrators-step',
      destinationsStep: 'company-wizard-destinations-step',
      downloadStep: 'company-wizard-download-step',
      templatesStep: 'company-wizard-templates-step'
    }
  };

  public nameStep = signal<CompanyNameStepValue>(CompanyNameStepDefaultValue);
  public administratorsStep = signal<CompanyAdministratorsStepValue>(CompanyAdministratorsStepDefaultValue);
  public backupTemplatesStep = signal<CompanyBackupTemplatesStepValue>(CompanyBackupTemplatesStepDefaultValue);
  public downloadStep = signal<CompanyDownloadStepValue>(CompanyDownloadStepDefaultValue);

  public readonly stepTitles = {
    administrators: this.getTitle('administrators'),
    destinations: this.getTitle('backupDestinations'),
    download: this.getTitle('downloadAndInstall'),
    name: this.getTitle('name'),
    summary: this.getTitle('summary'),
    templates: this.getTitle('backupTemplates')
  };

  public readonly loading: Signal<boolean> = computed(() => this.innerLoading() || this.companyWizardFacade.loading());

  // Bound as a field so the wizard can call it with the component as the context
  public readonly closeHandler = (): Observable<unknown> => {
    const step = this.backupTemplatesStepComponent();

    if (!step?.hasUnsavedChanges()) return of(null);

    return from(step.confirmApply()).pipe(
      catchError(() => of(false)),
      // A failed save emits nothing, and the wizard closes only on an emission - so the changes are not lost silently
      switchMap((confirmed) => {
        if (!confirmed) return of(null);

        if (step.invalidTemplates().length) return from(step.showInvalidTemplates()).pipe(switchMap(() => EMPTY));

        return step.applyChanges().pipe(catchError(() => EMPTY));
      })
    );
  };

  goToDestinationsHandler(): void {
    this.wizard()?.changeStep(-1);
  }

  forceNextStepWizard(isSave = false): void {
    const wizard = this.wizard();

    wizard.currentStep.valid = true;

    if (isSave) return wizard?.handleSave();

    wizard?.changeStep(1);
  }

  private getTitle(key: string): string {
    return this.i18nPipe.transform(`wizards:entityWizards.company.${key}`);
  }
}
